import _ from 'lodash';
import uuid from 'uuid';
import React, { useEffect } from 'react';
import { LineRange } from './namedEntityRecognitionSelection';
import {
  INamedEntityRecognitionResponse,
  INamedEntityRecognitionResponseAnnotation,
  INamedEntityRecognitionResponseAttribute,
  INamedEntityRecognitionResponseRelationship,
} from '@scale/shared/types/response/namedentityrecognition';
import { INamedEntityRecognitionRelationshipDefinition } from '@scale/shared/types/params/namedentityrecognition';
import { INamedEntityRecognitionLabel } from './namedEntityRecognitionLabels';
import { Line } from './namedEntityRecognitionParser';
import { captureClientSideError } from '@scale/frontend-lib/errors';
import {
  useNamedEntityRecognitionLinkingHelpers,
  augmentResponseRelationship,
} from './namedEntityRecognitionLinking';
import { AnnotationChange, AttributeChange } from '@scale/shared/annotation/types/AnnotationChange';
import { NERGlobalAttribute } from '@scale/shared/types/annotation';

export interface INamedEntityRecognitionAnnotation extends LineRange {
  id: string;
  labelId: string;
  text: string;
  attributes?: { [key: string]: INamedEntityRecognitionResponseAttribute };
}

export interface INamedEntityRecognitionRelationship
  extends INamedEntityRecognitionResponseRelationship {
  sourceAnnotation: INamedEntityRecognitionAnnotation;
  targetAnnotation: INamedEntityRecognitionAnnotation;
  // Same as start/endAnnotation, except ordered how they would be found in the task.
  topAnnotation: INamedEntityRecognitionAnnotation;
  bottomAnnotation: INamedEntityRecognitionAnnotation;
  relationshipDefinition: INamedEntityRecognitionRelationshipDefinition;
}

// "Annotation" is a bit overloaded here - it's being used both to refer to span annotations as well
// as to the entire holistic state - including relationships.
interface IAnnotationState {
  focusedAnnotationIds: Set<string>;
  current: INamedEntityRecognitionResponse;
  history: INamedEntityRecognitionResponse[];
  future: INamedEntityRecognitionResponse[];
}

function areAnnotationsOverlapping(
  a: INamedEntityRecognitionResponseAnnotation,
  b: INamedEntityRecognitionResponseAnnotation,
) {
  return _.inRange(a.start, b.start, b.end) || _.inRange(b.start, a.start, a.end);
}

function getAnnotationIndexWithStringWarning(
  annotation: INamedEntityRecognitionResponseAnnotation,
): [number, number] {
  let start: number, end: number;

  if (typeof annotation.start === 'string') {
    captureClientSideError(new Error('NER string index response found'), 'generic', {
      annotation,
    });

    start = parseInt(annotation.start, 10);
  } else {
    start = annotation.start;
  }

  if (typeof annotation.end === 'string') {
    captureClientSideError(new Error('NER string index response found'), 'generic', {
      annotation,
    });

    end = parseInt(annotation.end, 10);
  } else {
    end = annotation.end;
  }

  return [start, end];
}

function mapResponseToInternalAnnotations(
  responseAnnotations: INamedEntityRecognitionResponseAnnotation[],
  lines: Line[],
): INamedEntityRecognitionAnnotation[] {
  const internalAnnotations: INamedEntityRecognitionAnnotation[] = [];

  responseAnnotations.forEach(annotation => {
    const [annotationStart, annotationEnd] = getAnnotationIndexWithStringWarning(annotation);

    let startLineIdx = lines.findIndex(line => _.inRange(annotationStart, line.start, line.end));
    let endLineIdx = lines.findIndex(line =>
      _.inRange(annotationEnd, line.start + 1, line.end + 1),
    );

    // Filter out invalid annotations
    if (startLineIdx < 0 || endLineIdx < 0) {
      captureClientSideError(new Error('Invalid annotation for NER'), 'generic', {
        annotation,
      });
      // Instead of returning, show the corrupted annotations so that people can fix and remove them.
      startLineIdx = 0;
      endLineIdx = 0;
    }

    const startOffset = annotationStart - lines[startLineIdx].start;
    const endOffset = annotationEnd - lines[endLineIdx].start;

    internalAnnotations.push({
      id: annotation.id,
      labelId: annotation.label,
      text: annotation.text,
      startLineIdx,
      endLineIdx,
      startOffset,
      endOffset,
      attributes: annotation.attributes,
    });
  });
  return internalAnnotations;
}

export function useNamedEntityRecognition({
  readOnly,
  text,
  lines,
  labels = [],
  relationshipDefinitions = [],
  initialResponse,
  allowOverlappingAnnotations,
  globalAttributesDefinitions,
}: {
  readOnly: boolean;
  text: string;
  lines: Line[] | null;
  labels?: INamedEntityRecognitionLabel[];
  relationshipDefinitions?: INamedEntityRecognitionRelationshipDefinition[];
  initialResponse?: INamedEntityRecognitionResponse;
  allowOverlappingAnnotations?: boolean;
  globalAttributesDefinitions?: Record<string, NERGlobalAttribute>;
}) {
  const {
    getValidRelationshipsForAnnotationPair,
    getValidRelationshipTargets,
    isRelationshipStillValid,
  } = useNamedEntityRecognitionLinkingHelpers(relationshipDefinitions, labels);
  const [annotationsState, setAnnotationsStateUNSAFE] = React.useState<IAnnotationState>({
    focusedAnnotationIds: new Set(),
    current: {
      annotations:
        initialResponse?.annotations?.map(annotation => ({
          ...annotation,
          id: annotation.id ?? uuid.v4(),
        })) ?? [],
      relationships: initialResponse?.relationships ?? [],
      global_attributes: initialResponse?.global_attributes ?? {},
    },
    history: [],
    future: [],
  });

  useEffect(() => {
    if (!lines) return;

    setAnnotationsStateUNSAFE(prev => {
      const updatedAnnotations = prev.current.annotations
        .map(annotation => {
          const [start, end] = getAnnotationIndexWithStringWarning(annotation);
          // Find new line indices for the annotation
          const startLineIdx = lines.findIndex(line => _.inRange(start, line.start, line.end));
          const endLineIdx = lines.findIndex(line => _.inRange(end, line.start + 1, line.end + 1));
          // If annotation is no longer valid in new text, remove it
          if (startLineIdx < 0 || endLineIdx < 0) {
            return undefined;
          }
          // Get the new text for this annotation
          const newText = text.slice(start, end);
          return {
            ...annotation,
            text: newText,
            start,
            end,
          };
        })
        .filter(
          (annotation): annotation is INamedEntityRecognitionResponseAnnotation =>
            annotation !== undefined,
        );

      return {
        ...prev,
        current: {
          ...prev.current,
          annotations: updatedAnnotations,
        },
      };
    });
  }, [text, lines]);

  const {
    annotations: currentAnnotations,
    relationships: currentRelationships,
    global_attributes: currentGlobalAttributes,
  } = annotationsState.current;

  const internalGlobalAttributes = React.useMemo(
    () => currentGlobalAttributes,
    [currentGlobalAttributes],
  );

  const internalAnnotations = React.useMemo(
    () => (lines ? mapResponseToInternalAnnotations(currentAnnotations, lines) : []),
    [lines, currentAnnotations],
  );

  const internalRelationships: INamedEntityRecognitionRelationship[] = React.useMemo(
    () =>
      _.compact(
        _.map(currentRelationships, relationship =>
          augmentResponseRelationship(
            relationship,
            internalAnnotations,
            relationshipDefinitions.find(({ name }) => relationship.name === name)!,
          ),
        ),
      ),
    [currentRelationships, relationshipDefinitions, internalAnnotations],
  );

  const setAnnotationsState: React.Dispatch<React.SetStateAction<IAnnotationState>> = newState => {
    if (readOnly) return;

    setAnnotationsStateUNSAFE(newState);
  };

  function unFocusAnnotation() {
    setAnnotationsStateUNSAFE(prev => ({ ...prev, focusedAnnotationIds: new Set() }));
  }

  function focusAnnotationById(annotationId: string, multiple = false) {
    setAnnotationsStateUNSAFE(prev => {
      if (multiple) {
        const newSet = new Set(prev.focusedAnnotationIds);
        newSet.add(annotationId);

        return {
          ...prev,
          focusedAnnotationIds: newSet,
        };
      }

      return {
        ...prev,
        focusedAnnotationIds: new Set([annotationId]),
      };
    });
  }

  const focusedAnnotations = React.useMemo(
    () =>
      annotationsState.current.annotations.filter(x =>
        annotationsState.focusedAnnotationIds.has(x.id),
      ),
    [annotationsState],
  );

  function focusAnnotationJump(dir: 'backward' | 'forward') {
    setAnnotationsStateUNSAFE(prev => {
      if (!prev.current.annotations[0]) {
        return prev;
      }

      const start = 0;
      const end = prev.current.annotations.length - 1;

      const annotationsSorted = _.sortBy(prev.current.annotations, annotation => annotation.start);

      if (!prev.focusedAnnotationIds.size) {
        return {
          ...prev,
          focusedAnnotationIds: new Set([annotationsSorted[dir === 'forward' ? start : end].id]),
        };
      }

      const currIndex = annotationsSorted.findIndex(x => prev.focusedAnnotationIds.has(x.id));
      const nextAnnotation = annotationsSorted[dir === 'forward' ? currIndex + 1 : currIndex - 1];

      return {
        ...prev,
        focusedAnnotationIds: new Set([
          nextAnnotation
            ? nextAnnotation.id
            : dir === 'forward'
            ? annotationsSorted[start].id
            : annotationsSorted[end].id,
        ]),
      };
    });
  }

  function addAnnotationFromRange(range: LineRange, label: INamedEntityRecognitionLabel) {
    if (_.isNil(lines)) {
      return;
    }

    const start = lines[range.startLineIdx].start + range.startOffset;
    const end = lines[range.endLineIdx].start + range.endOffset;

    const annotation: INamedEntityRecognitionResponseAnnotation = {
      id: uuid.v4(),
      label: label.name,
      text: text.slice(start, end),
      start,
      end,
    };

    if (!allowOverlappingAnnotations) {
      const alreadyExist = annotationsState.current.annotations.some(x =>
        areAnnotationsOverlapping(x, annotation),
      );

      if (alreadyExist) {
        return;
      }
    }

    setAnnotationsState(prev => ({
      focusedAnnotationIds: new Set([annotation.id]),
      future: [],
      history: [...prev.history, prev.current],
      current: {
        ...prev.current,
        annotations: [...prev.current.annotations, annotation],
      },
    }));

    const windowSelection = window.getSelection();
    if (windowSelection) {
      windowSelection.removeAllRanges();
    }

    return annotation;
  }

  function updateAnnotationLabelById(annotationId: string, label: string) {
    setAnnotationsState(prev => {
      return {
        future: [],
        focusedAnnotationIds: prev.focusedAnnotationIds,
        history: [...prev.history, prev.current],
        current: {
          ...prev.current,
          annotations: prev.current.annotations?.map(annotation => {
            if (annotation.id === annotationId) {
              return {
                ...annotation,
                label,
              };
            }

            return annotation;
          }),
          relationships: prev.current.relationships?.filter(relationship => {
            const internalRelationship = internalRelationships.find(
              ({ id }) => id === relationship.id,
            )!;
            return isRelationshipStillValid(internalRelationship, annotationId, label);
          }),
        },
      };
    });
  }

  function updateAnnotationAttributeById(
    annotationId: string,
    key: string,
    value: INamedEntityRecognitionResponseAttribute,
  ) {
    setAnnotationsState(prev => {
      return {
        future: [],
        focusedAnnotationIds: prev.focusedAnnotationIds,
        history: [...prev.history, prev.current],
        current: {
          ...prev.current,
          annotations: prev.current.annotations.map(annotation => {
            if (annotation.id === annotationId) {
              return {
                ...annotation,
                attributes: {
                  ...annotation.attributes,
                  [key]: value,
                },
              };
            }

            return annotation;
          }),
        },
      };
    });
  }

  function removeFocusedAnnotation() {
    setAnnotationsState(prev => {
      if (prev.focusedAnnotationIds.size) {
        return {
          focusedAnnotationIds: new Set([]),
          history: [...prev.history, prev.current],
          current: {
            ...prev.current,
            annotations: prev.current.annotations.filter(x => !prev.focusedAnnotationIds.has(x.id)),
            relationships: prev.current.relationships?.filter(
              x =>
                !prev.focusedAnnotationIds.has(x.source_ref) &&
                !prev.focusedAnnotationIds.has(x.target_ref),
            ),
          },
          future: [],
        };
      }

      return prev;
    });
  }

  function removeAnnotation(annotationId: string) {
    setAnnotationsState(prev => {
      const focusedAnnotationIds = new Set(prev.focusedAnnotationIds);
      focusedAnnotationIds.delete(annotationId);

      return {
        focusedAnnotationIds,
        history: [...prev.history, prev.current],
        current: {
          ...prev.current,
          annotations: prev.current.annotations.filter(x => x.id !== annotationId),
          relationships: prev.current.relationships?.filter(
            x => x.source_ref !== annotationId && x.target_ref !== annotationId,
          ),
        },
        future: [],
      };
    });
  }

  // Used for both adding and modifying relationships.
  function setRelationship(
    relationship: INamedEntityRecognitionRelationship,
    relationshipId?: string,
  ) {
    const truncatedRelationship = _.pick(relationship, ['id', 'source_ref', 'target_ref', 'name']);
    setAnnotationsState(prev => ({
      ...prev,
      history: [...prev.history, prev.current],
      current: {
        ...prev.current,
        relationships: [
          ...(prev.current.relationships?.filter(({ id }) => id !== relationshipId) ?? []),
          truncatedRelationship,
        ],
      },
      future: [],
    }));
  }

  function removeRelationship(relationshipId: string) {
    setAnnotationsState(prev => ({
      ...prev,
      history: [...prev.history, prev.current],
      current: {
        ...prev.current,
        relationships: prev.current.relationships?.filter(x => x.id !== relationshipId),
      },
      future: [],
    }));
  }

  function handleGlobalAttributeChange(
    _annotationId: string | undefined,
    change: AnnotationChange,
    globalAttributes?: any,
  ) {
    let newGlobalAttributes =
      globalAttributes ?? _.cloneDeep(annotationsState.current.global_attributes);
    const castedChange = change as AttributeChange;
    for (const key in globalAttributesDefinitions) {
      const conditions = globalAttributesDefinitions[key].conditions?.attribute_conditions ?? [];
      for (const condition of conditions) {
        if (Object.keys(condition)[0] === castedChange.attrKey)
          newGlobalAttributes = handleGlobalAttributeChange(
            undefined,
            {
              attrKey: key,
              attrVal: undefined,
            },
            newGlobalAttributes,
          );
      }
    }
    if (castedChange.attrKey !== undefined && newGlobalAttributes !== undefined) {
      newGlobalAttributes[castedChange.attrKey] = castedChange.attrVal;
    }
    setAnnotationsState(prev => ({
      ...prev,
      history: [...prev.history, prev.current],
      current: {
        ...prev.current,
        global_attributes: newGlobalAttributes,
      },
    }));
    return newGlobalAttributes;
  }

  function undo() {
    setAnnotationsState(state =>
      state.history.length
        ? {
            focusedAnnotationIds: new Set([]),
            future: [...state.future, state.current],
            current: state.history[state.history.length - 1],
            history: state.history.slice(0, state.history.length - 1),
          }
        : state,
    );
  }

  function redo() {
    setAnnotationsState(state =>
      state.future.length
        ? {
            focusedAnnotationIds: new Set([]),
            history: [...state.history, state.current],
            current: state.future[state.future.length - 1],
            future: state.future.slice(0, state.future.length - 1),
          }
        : state,
    );
  }

  function clearAnnotations() {
    setAnnotationsState(prev => ({
      focusedAnnotationIds: new Set([]),
      future: [],
      history: [...prev.history, prev.current],
      current: { annotations: [], relationships: [] },
    }));
  }

  // Used for initializing multimodal tasks
  function setInitialAnnotations(
    rawAnnotations:
      | INamedEntityRecognitionResponseAnnotation
      | INamedEntityRecognitionResponseAnnotation[],
  ) {
    let annotations: INamedEntityRecognitionResponseAnnotation[];
    if (Array.isArray(rawAnnotations)) {
      annotations = rawAnnotations;
    } else {
      annotations = [rawAnnotations];
    }

    setAnnotationsState(prev => ({
      ...prev,
      future: [],
      history: [],
      current: {
        annotations: annotations.map(annotation => ({
          ...annotation,
          id: annotation.id ?? uuid.v4(),
        })),
      },
    }));
  }

  function checkRequiredAttributeExist(annotationId: string, key: string) {
    const annotationIndex = annotationsState.current.annotations.findIndex(
      x => x.id === annotationId,
    );
    if (annotationIndex !== -1) {
      return (
        annotationsState.current.annotations[annotationIndex].attributes !== undefined &&
        annotationsState.current.annotations[annotationIndex].attributes![key] !== undefined
      );
    }
    return false;
  }

  return {
    focusedAnnotationIds: annotationsState.focusedAnnotationIds,
    focusedAnnotations,
    focusAnnotationById,
    focusAnnotationJump,
    unFocusAnnotation,
    annotations: internalAnnotations,
    response: annotationsState.current,
    relationships: internalRelationships,
    globalAttributes: internalGlobalAttributes,
    addAnnotationFromRange,
    updateAnnotationLabelById,
    updateAnnotationAttributeById,
    removeAnnotation,
    removeFocusedAnnotation,
    setRelationship,
    removeRelationship,
    handleGlobalAttributeChange,
    undo,
    redo,
    canUndo: annotationsState.history.length > 0,
    canRedo: annotationsState.future.length > 0,
    clearAnnotations,
    getValidRelationshipsForAnnotationPair,
    getValidRelationshipTargets,
    setInitialAnnotations,
    checkRequiredAttributeExist,
  };
}
